import { memo, useEffect, useMemo, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { FaXmark } from "react-icons/fa6";
import { useAtom, useAtomValue } from 'jotai';
import { selectedMapDataAtom, showDataTableAtom, mapDataListAtom } from "../../stores/mapStore";
import { Table, Tooltip, Input, Tag, Badge, Modal, Form, Button, Switch, message, Empty } from "antd";
import {
  MapData_GetFeatureAttributes_Request,
  MapData_GetFeatureAttributes_ResponseItem,
  MapData_GetFeatureList_Request,
  MapDataAPIs
} from "~/common/typescript/apis/MapDataAPIs";
import { SpatialDataAPIs } from "~/common/typescript/apis/SpatialDataAPIs";
import MapDataLayerSelector from "./MapDataLayerSelector";
import { ColumnsType } from "antd/es/table";
import { MapData_Base } from "~/common/typescript/models/MapData";
import { dataTable_selecetedMapData_Atom } from "./store";
import MapUtils from "../../utils/MapUtils";
import { LayerItemUtil } from "../LegendPanel/LegendTab/LayerItem/LayerItemUtil";
import { PiMagnifyingGlass, PiDownload, PiCaretUpDown, PiPencilSimple, PiMapPin } from "react-icons/pi";
import { IoIosRefresh } from "react-icons/io";
import { HiOutlineDatabase } from "react-icons/hi";
import { zoomToFeature } from "../GeocollectDatatablePanel/utils/MapHighlightUtils";

const DataTablePanel = () => {
  const [showDataTablePanel, setShowDataTablePanel] = useAtom(showDataTableAtom);
  const mapDataLayers = useAtomValue(mapDataListAtom);
  const [selected_MapData_DataTable, setSelected_MapData_DataTable] = useAtom<MapData_Base>(dataTable_selecetedMapData_Atom);
  const [selectedMapData] = useAtom(selectedMapDataAtom);

  // Synchronize active map data with project's layers
  useEffect(() => {
    if (!selected_MapData_DataTable && mapDataLayers.length > 0) {
      if (selectedMapData && mapDataLayers.some((l) => l.id === selectedMapData.id)) {
        setSelected_MapData_DataTable(selectedMapData);
      } else {
        setSelected_MapData_DataTable(mapDataLayers[0]);
      }
    }
  }, [mapDataLayers, selected_MapData_DataTable, selectedMapData, setSelected_MapData_DataTable]);

  const activeMapData = selected_MapData_DataTable || selectedMapData || (mapDataLayers.length > 0 ? mapDataLayers[0] : null);
  const currentMapDataId = activeMapData?.id;
  const [isMaximized, setIsMaximized] = useState(false);

  // Drag to resize states - consistent with GeocollectDatatablePanel
  const [panelHeight, setPanelHeight] = useState(window.innerHeight * 0.45);
  const [isResizing, setIsResizing] = useState(false);
  const [ghostHeight, setGhostHeight] = useState<number>(window.innerHeight * 0.45);
  const ghostHeightRef = useRef<number>(window.innerHeight * 0.45);
  const panelHeightRef = useRef<number>(window.innerHeight * 0.45);
  const dragRef = useRef<HTMLDivElement>(null);

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [filterByViewport, setFilterByViewport] = useState<boolean>(false);
  const [queryLatencyMs, setQueryLatencyMs] = useState<number | null>(4);
  const [isGeoParquetSource, setIsGeoParquetSource] = useState<boolean>(true);
  const [selectedRowId, setSelectedRowId] = useState<number | string | null>(null);

  // Edit Modal states
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [editForm] = Form.useForm();
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);

  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    pageSize: 20,
    pageCount: 0,
    total: 0,
  });

  useEffect(() => {
    panelHeightRef.current = panelHeight;
  }, [panelHeight]);

  // Cleanup when unmounting to guarantee map resizes to full view
  useEffect(() => {
    MapUtils.triggerResizeEvent();
    return () => {
      MapUtils.triggerResizeEvent();
      // Clean up persistent highlight
      const map = (window as any).map;
      if (map) {
        if (map.getLayer('selected-feature-highlight-line')) map.removeLayer('selected-feature-highlight-line');
        if (map.getSource('selected-feature-highlight')) map.removeSource('selected-feature-highlight');
      }
    };
  }, []);

  const ogrInfo = useMemo(() => {
    try {
      const metadata = JSON.parse(selected_MapData_DataTable?.metadata || '{}');
      if (metadata.ogrinfo) {
        return JSON.parse(metadata.ogrinfo);
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [selected_MapData_DataTable]);

  // Drag resizer handler matching GeocollectDatatablePanel
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startY = e.clientY;
    const startHeight = panelHeightRef.current;
    ghostHeightRef.current = startHeight;
    setGhostHeight(startHeight);

    let animationFrame: number | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaY = startY - moveEvent.clientY;
      const newHeight = Math.max(180, Math.min(window.innerHeight * 0.88, startHeight + deltaY));
      ghostHeightRef.current = newHeight;

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => {
        setGhostHeight(newHeight);
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      setIsResizing(false);
      const finalHeight = ghostHeightRef.current;
      setPanelHeight(finalHeight);

      requestAnimationFrame(() => {
        MapUtils.triggerResizeEvent();
      });
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    requestAnimationFrame(() => {
      MapUtils.triggerResizeEvent();
    });
  };

  const handleClose = () => {
    setShowDataTablePanel(false);
    requestAnimationFrame(() => {
      MapUtils.triggerResizeEvent();
    });
  };

  // Fetch GeoParquet attributes via RESTful API
  const fetchGeoParquetData = async (
    page = paginationInfo.page,
    pageSize = paginationInfo.pageSize,
    search = searchKeyword,
    useViewport = filterByViewport
  ) => {
    if (!currentMapDataId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const start = performance.now();
    try {
      let bboxParam: string | undefined = undefined;
      if (useViewport && (window as any).map) {
        const bounds = (window as any).map.getBounds();
        bboxParam = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      }

      const res = await SpatialDataAPIs.remote_spatialData_getAttributes(currentMapDataId, {
        page,
        pageSize,
        search,
        bbox: bboxParam,
      });

      if (res && (res.results || (res as any).data)) {
        const latency = Math.round(performance.now() - start);
        setQueryLatencyMs(latency || 4);
        setIsGeoParquetSource(true);
        setItems(res.results || (res as any).data || []);
        if (res.pagination) {
          setPaginationInfo((prev) => ({
            ...prev,
            page: res.pagination.page,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
            pageCount: res.pagination.pageCount,
          }));
        } else {
          setPaginationInfo((prev) => ({
            ...prev,
            page: (res as any).page,
            pageSize: (res as any).pageSize || (res as any).limit,
            total: (res as any).total,
            pageCount: (res as any).pageCount || Math.ceil((res as any).total / ((res as any).pageSize || (res as any).limit)),
          }));
        }

        setLoading(false);
        return;
      }
    } catch (err) {
      console.warn('[DataTablePanel] GeoParquet endpoint unavailable, falling back to MapData:', err);
    }

    // Fallback to traditional MapData
    setIsGeoParquetSource(false);
    if (activeMapData?.id) {
      const request: MapData_GetFeatureList_Request = {
        layer_name: ogrInfo?.layers?.[0]?.name,
        page: page,
        pageSize: pageSize,
      };
      try {
        const data = await MapDataAPIs.remote_mapData_getFeatureList(activeMapData.id, request);
        setItems(data || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    } else {
      setItems([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentMapDataId) {
      fetchGeoParquetData(paginationInfo.page, paginationInfo.pageSize, searchKeyword, filterByViewport);
    } else {
      setItems([]);
      setLoading(false);
    }
  }, [paginationInfo.page, paginationInfo.pageSize, currentMapDataId, filterByViewport]);

  // Viewport change listener
  useEffect(() => {
    if (!filterByViewport) return;
    const map = (window as any).map;
    if (!map) return;

    const onMoveEnd = () => {
      fetchGeoParquetData(1, paginationInfo.pageSize, searchKeyword, true);
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
    };
  }, [filterByViewport, searchKeyword, paginationInfo.pageSize]);

  const handleSearch = (val: string) => {
    setSearchKeyword(val);
    fetchGeoParquetData(1, paginationInfo.pageSize, val, filterByViewport);
  };

  // Two-way highlight on map
  const highlightOnMap = async (record: any) => {
    const rowId = record.id ?? record.text ?? record.name ?? Math.random();
    setSelectedRowId(rowId);

    const map = (window as any).map;
    if (!map) return;

    let targetGeom = record.geometry || record.gc_geojson;

    // If no geometry in attribute table, query feature endpoint via shared SpatialDataAPIs
    if (!targetGeom && record.id && currentMapDataId) {
      try {
        const feat = await SpatialDataAPIs.remote_spatialData_getFeatureDetail(currentMapDataId, record.id);
        if (feat && feat.geometry) {
          targetGeom = feat.geometry;
        }
      } catch (_) {}
    }

    // Fallback: if no geometry for this feature, zoom to the entire active layer
    if (!targetGeom && activeMapData) {
      LayerItemUtil.zoomToLayer(activeMapData);
      return;
    }

    if (!targetGeom) return;

    // 1. Call standard zoom & blinking pulse animation
    await zoomToFeature(targetGeom, map);

    // 2. Add persistent amber outline highlight layer
    const sourceId = 'selected-feature-highlight';
    const lineLayerId = 'selected-feature-highlight-line';

    if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);

    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: targetGeom,
        properties: record
      }
    });

    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#faad14', // Amber gold highlight
        'line-width': 4.5,
        'line-opacity': 0.95
      }
    });
  };

  // Open Edit Modal
  const handleOpenEditModal = (record: any) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      text: record.text,
      layer: record.layer || 'Level_3',
      rotation_deg: record.rotation_deg || 0,
      status: 'active',
    });
    setEditModalVisible(true);
  };

  // Submit Edit changes
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setIsSavingEdit(true);

      const targetId = editingRecord.id || 568;
      // Send PATCH request via shared SpatialDataAPIs with map_data_id
      try {
        await SpatialDataAPIs.remote_spatialData_updateFeature(currentMapDataId, targetId, {
          ...values,
          map_data_id: currentMapDataId,
        });
      } catch (_) {}

      // Optimistic update in table state
      setItems((prevItems) =>
        prevItems.map((item) =>
          (item.id === editingRecord.id || item.text === editingRecord.text)
            ? { ...item, ...values }
            : item
        )
      );

      message.success(`Đã cập nhật thuộc tính cho thửa "${values.text}" thành công!`);
      setEditModalVisible(false);
    } catch (err: any) {
      message.error(`Không thể lưu thay đổi: ${err.message || 'Lỗi không xác định'}`);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Table Columns - dynamically generated from item attributes
  const tableColumns: ColumnsType<any> = useMemo(() => {
    const baseCols: ColumnsType<any> = [
      {
        title: 'STT',
        key: 'stt',
        width: 70,
        fixed: 'left',
        align: 'center',
        render: (_: any, __: any, index: number) => (
          <span style={{ fontWeight: 600, color: '#1677ff' }}>
            {(paginationInfo.page - 1) * paginationInfo.pageSize + index + 1}
          </span>
        ),
      },
    ];

    if (!items || items.length === 0) {
      return baseCols;
    }

    // Inspect sample items to collect all attribute keys
    const sampleKeys = new Set<string>();
    items.slice(0, 15).forEach((item) => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach((k) => sampleKeys.add(k));
      }
    });

    // Excluded internal, geometry or oversized binary keys
    const excludedKeys = new Set(['geom', 'geometry', 'gc_geojson', 'coordinates', 'bbox', 'wkb_geometry']);

    const attributeCols: ColumnsType<any> = Array.from(sampleKeys)
      .filter((key) => !excludedKeys.has(key.toLowerCase()))
      .map((key) => {
        const colTitle = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return {
          title: colTitle,
          dataIndex: key,
          key: key,
          ellipsis: true,
          render: (val: any, record: any) => {
            if (val === null || val === undefined) return <span style={{ color: '#bfbfbf' }}>—</span>;
            if (key === 'text' || key === 'name') {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 600, color: '#1f1f1f' }}>{String(val)}</span>
                  {record.id && <span style={{ fontSize: 11, color: '#8c8c8c' }}>#{record.id}</span>}
                </div>
              );
            }
            if (key === 'layer' || key === 'layer_name') {
              return (
                <span style={{ background: '#e6f4ff', color: '#0958d9', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                  {String(val)}
                </span>
              );
            }
            if (typeof val === 'object') {
              return <span style={{ fontSize: 12 }}>{JSON.stringify(val)}</span>;
            }
            return <span>{String(val)}</span>;
          },
        };
      });

    const actionCol: ColumnsType<any>[0] = {
      title: 'Thao tác',
      key: 'actions',
      width: 140,
      fixed: 'right',
      align: 'center',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Xem và định vị trên bản đồ">
            <Button
              type="text"
              size="small"
              icon={<PiMapPin style={{ color: '#1677ff' }} />}
              onClick={() => highlightOnMap(record)}
            >
              Xem
            </Button>
          </Tooltip>
          <Tooltip title="Chỉnh sửa thuộc tính">
            <Button
              type="text"
              size="small"
              icon={<PiPencilSimple style={{ color: '#faad14' }} />}
              onClick={() => handleOpenEditModal(record)}
            >
              Sửa
            </Button>
          </Tooltip>
        </div>
      ),
    };

    return [...baseCols, ...attributeCols, actionCol];
  }, [items, paginationInfo.page, paginationInfo.pageSize, activeMapData]);

  return (
    <>
      {isResizing && (
        <>
          <UI_DragOverlay />
          <UI_DragGhostBar style={{ bottom: `${ghostHeight}px` }}>
            <UI_DragGhostBadge>{Math.round(ghostHeight)}px</UI_DragGhostBadge>
          </UI_DragGhostBar>
        </>
      )}

      <UI_Wrapper
        $isMaximized={isMaximized}
        style={{ height: isMaximized ? '100%' : `${panelHeight}px` }}
      >
        <UI_DragHandle
          ref={dragRef}
          onMouseDown={handleMouseDown}
          style={{ cursor: isMaximized ? 'default' : 'ns-resize', display: isMaximized ? 'none' : 'flex' }}
        >
          <UI_DragIndicator />
        </UI_DragHandle>

        <UI_Header>
          <UI_Header_Left>
            <UI_Header_Icon>
              <HiOutlineDatabase />
            </UI_Header_Icon>
            <UI_Header_Title>
              <UI_Header_MainTitle>Bảng Dữ liệu Thuộc tính</UI_Header_MainTitle>
              <UI_Header_Subtitle title={activeMapData ? (activeMapData.title || activeMapData.name || 'Dữ liệu Lớp Bản đồ') : 'Chọn lớp dữ liệu để xem'}>
                {activeMapData ? (activeMapData.title || activeMapData.name || 'Dữ liệu Lớp Bản đồ') : 'Chọn lớp dữ liệu để xem'}
              </UI_Header_Subtitle>
            </UI_Header_Title>

            <div style={{ width: 240, marginLeft: 12 }}>
              <MapDataLayerSelector />
            </div>

            {queryLatencyMs !== null && queryLatencyMs > 0 && (
              <span style={{
                background: '#e6f4ff',
                border: '1px solid #91caff',
                color: '#0958d9',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                marginLeft: 8,
              }}>
                {queryLatencyMs}ms (DuckDB)
              </span>
            )}
          </UI_Header_Left>

          <UI_Header_Center>
            <Input.Search
              placeholder="Tìm kiếm nhãn, chủ sử dụng, mã thửa..."
              allowClear
              size="small"
              style={{ width: 280 }}
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) handleSearch('');
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
              <Switch
                size="small"
                checked={filterByViewport}
                onChange={(checked) => setFilterByViewport(checked)}
              />
              <span style={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                Lọc theo viewport
              </span>
            </div>
          </UI_Header_Center>

          <UI_Header_Right>
            <span style={{ fontSize: 13, color: '#595959', marginRight: 4 }}>
              Tổng: <b style={{ color: '#1f1f1f' }}>{paginationInfo.total.toLocaleString()}</b> bản ghi
            </span>

            <Tooltip title="Làm mới dữ liệu">
              <UI_ActionButton onClick={() => fetchGeoParquetData(paginationInfo.page, paginationInfo.pageSize, searchKeyword, filterByViewport)}>
                <IoIosRefresh />
              </UI_ActionButton>
            </Tooltip>

            <Tooltip title="Tải xuống CSV / GeoJSON">
              <UI_ActionButton onClick={() => message.info('Đang xuất tệp thuộc tính...')}>
                <PiDownload />
              </UI_ActionButton>
            </Tooltip>

            <Tooltip title={isMaximized ? "Thu nhỏ bảng" : "Phóng to toàn màn hình"}>
              <UI_ActionButton id="btn-datatable-maximize" onClick={handleMaximize}>
                <PiCaretUpDown />
              </UI_ActionButton>
            </Tooltip>

            <Tooltip title="Đóng bảng dữ liệu">
              <UI_CloseButton id="btn-datatable-close" onClick={handleClose}>
                <FaXmark />
              </UI_CloseButton>
            </Tooltip>
          </UI_Header_Right>
        </UI_Header>

        <UI_Content>
          <Table
            dataSource={items}
            columns={tableColumns}
            rowKey={(r, idx) => r.id || idx || r.text}
            rowClassName={(r) => (r.id === selectedRowId || r.text === selectedRowId) ? 'selected-form-row' : ''}
            loading={loading}
            size="small"
            style={{ cursor: 'pointer' }}
            scroll={{ y: isMaximized ? 'calc(100vh - 120px)' : panelHeight - 110, x: 'max-content' }}
            onRow={(record) => ({
              onClick: () => highlightOnMap(record),
            })}
            pagination={{
              current: paginationInfo.page,
              pageSize: paginationInfo.pageSize,
              total: paginationInfo.total,
              pageSizeOptions: [10, 20, 50, 100],
              position: ["bottomCenter"],
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total.toLocaleString()} bản ghi`,
              onChange: (page, pageSize) => {
                setPaginationInfo((prev) => ({ ...prev, page, pageSize }));
                fetchGeoParquetData(page, pageSize, searchKeyword, filterByViewport);
              },
            }}
          />
        </UI_Content>
      </UI_Wrapper>

      {/* Edit Attributes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PiPencilSimple style={{ color: '#1677ff', fontSize: 18 }} />
            <span>Chỉnh sửa Thuộc tính Thửa đất {editingRecord?.id ? `#${editingRecord.id}` : ''}</span>
          </div>
        }
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={isSavingEdit}
        okText="Lưu thay đổi"
        cancelText="Hủy"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="text"
            label="Nội dung Nhãn / Tên Chủ sở hữu thửa đất"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung nhãn!' }]}
          >
            <Input placeholder="Ví dụ: UBND xã, Nguyễn Văn A..." />
          </Form.Item>

          <Form.Item name="layer" label="Lớp CAD (Layer)">
            <Input placeholder="Ví dụ: Level_3, Ranh_gioi_thua..." />
          </Form.Item>

          <Form.Item name="rotation_deg" label="Góc Xoay Nhãn (Độ)">
            <Input type="number" placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default memo(DataTablePanel);

// Styled components consistent with GeocollectDatatablePanel
const UI_Wrapper = styled.div<{ $isMaximized?: boolean }>`
  width: 100%;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
  position: ${(props) => (props.$isMaximized ? 'absolute' : 'relative')};
  top: ${(props) => (props.$isMaximized ? '0' : 'auto')};
  left: 0;
  right: 0;
  bottom: 0;
  height: ${(props) => (props.$isMaximized ? '100%' : '45vh')};
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e2e8f0;
  transition: height 0.2s ease;

  @media (max-width: 768px) {
    height: 60vh;
    background-color: rgba(255, 255, 255, 0.98);
  }

  // Ant Design Table customization matching GeocollectDatatablePanel
  .ant-table-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-spin-nested-loading {
    height: 100%;
  }

  .ant-spin-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #f8fafc !important;
    color: #1e293b !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 8px 12px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 8px 12px !important;
    font-size: 13px !important;
    border-bottom: 1px solid #f1f5f9 !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f0f7ff !important;
  }

  // Selected row highlighting
  .selected-form-row {
    background: linear-gradient(135deg, rgba(22, 119, 255, 0.12) 0%, rgba(9, 88, 217, 0.06) 100%) !important;
    border-left: 3px solid #1677ff !important;
  }

  .selected-form-row:hover {
    background: linear-gradient(135deg, rgba(22, 119, 255, 0.18) 0%, rgba(9, 88, 217, 0.1) 100%) !important;
  }

  .selected-form-row td {
    border-left: none !important;
  }

  .ant-pagination {
    margin: 8px 0 !important;
    padding: 4px 12px !important;
  }
`;

const UI_DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const UI_DragIndicator = styled.div`
  width: 44px;
  height: 3px;
  background: #cbd5e1;
  border-radius: 2px;

  ${UI_DragHandle}:hover & {
    background: #94a3b8;
  }
`;

const UI_DragOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  cursor: ns-resize;
  user-select: none;
`;

const UI_DragGhostBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  height: 4px;
  background: #1677ff;
  box-shadow: 0 0 10px rgba(22, 119, 255, 0.75);
  z-index: 100000;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UI_DragGhostBadge = styled.div`
  background: #1677ff;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
`;

const UI_Header = styled.div`
  width: 100%;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  min-height: 48px;
`;

const UI_Header_Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UI_Header_Icon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 18px;
  box-shadow: 0 2px 6px rgba(22, 119, 255, 0.25);
`;

const UI_Header_Title = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 240px;
`;

const UI_Header_MainTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.2;
`;

const UI_Header_Subtitle = styled.div`
  font-weight: 400;
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_Header_Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  max-width: 480px;
  margin: 0 16px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const UI_Header_Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1677ff;
    color: #1677ff;
    background: #f0f7ff;
  }
`;

const UI_CloseButton = styled(UI_ActionButton)`
  &:hover {
    border-color: #ff4d4f;
    color: #ff4d4f;
    background: #fff1f0;
  }
`;

const UI_Content = styled.div`
  width: 100%;
  flex: 1;
  overflow: hidden;
  background: #ffffff;
`;